import React, { useState, useEffect } from 'react';
import { Calendar, Badge, Card, Button, message, Modal, Form, Select, DatePicker, TimePicker, Input, Radio, Row, Col, Divider } from 'antd';
import { PlusOutlined, UserAddOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { appointmentService, patientService, dentistService } from '../../services';
import { useApp } from '../../context/AppContext';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const Appointments = () => {
    const { t } = useTranslation(['appointment', 'common', 'patient']);
    const { user } = useApp();
    const isDentist = user?.role === 'dentist';
    const [appointments, setAppointments] = useState([]);
    const [patients, setPatients] = useState([]);
    const [dentists, setDentists] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [patientType, setPatientType] = useState('existing'); // 'existing' or 'new'
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchAppointments();
        fetchPatients();
        fetchDentists();
    }, []);

    const fetchAppointments = async () => {
        try {
            const response = await appointmentService.getAll({});
            if (response.success) {
                setAppointments(response.data);
            }
        } catch (error) {
            message.error(t('appointment:messages.fetchFailed'));
        }
    };

    const fetchPatients = async () => {
        try {
            const response = await patientService.getAll({});
            if (response.success) {
                setPatients(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch patients');
        }
    };

    const fetchDentists = async () => {
        try {
            const response = await dentistService.getAll({ limit: 1000, isActive: true });
            if (response.success) {
                setDentists(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch dentists');
            message.error(t('common:errors.fetchFailed', 'Failed to fetch dentists'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);
            let patientId = values.patientId;
            let assignedDentistId = isDentist ? values.dentistId : values.dentistId;

            // If new patient, create the patient first
            if (patientType === 'new') {
                const newPatientData = {
                    name: values.newPatientName,
                    age: values.newPatientAge,
                    gender: values.newPatientGender,
                    contact: values.newPatientContact,
                    email: values.newPatientEmail || undefined,
                    // Assign the patient to the dentist who will handle the appointment
                    dentistIds: [assignedDentistId]
                };
                const patientResponse = await patientService.create(newPatientData);
                if (patientResponse.success) {
                    patientId = patientResponse.data._id;
                    message.success(t('patient:messages.createSuccess'));
                    // Refresh patient list
                    fetchPatients();
                } else {
                    message.error(t('patient:messages.operationFailed'));
                    return;
                }
            }

            const appointmentData = {
                patientId,
                date: values.date.format('YYYY-MM-DD'),
                time: values.time.format('HH:mm'),
                type: values.type,
                reasonForVisit: values.reasonForVisit,
                notes: values.notes,
            };
            if (!isDentist) {
                appointmentData.dentistId = assignedDentistId;
            }

            await appointmentService.create(appointmentData);
            message.success(t('appointment:messages.createSuccess'));
            setModalVisible(false);
            setPatientType('existing');
            form.resetFields();
            fetchAppointments();
        } catch (error) {
            message.error(error.response?.data?.message || t('appointment:messages.createFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const getListData = (value) => {
        const dateStr = value.format('YYYY-MM-DD');
        return appointments.filter(apt =>
            dayjs(apt.date).format('YYYY-MM-DD') === dateStr
        );
    };

    const dateCellRender = (value) => {
        const listData = getListData(value);
        return (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {listData.slice(0, 3).map((item) => (
                    <li key={item._id}>
                        <Badge
                            status={item.status === 'Confirmed' ? 'success' : 'processing'}
                            text={`${item.time} - ${item.patientId?.name}`}
                            style={{ fontSize: 11 }}
                        />
                    </li>
                ))}
                {listData.length > 3 && <li style={{ fontSize: 11, color: '#999' }}>+{listData.length - 3} more</li>}
            </ul>
        );
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setPatientType('existing');
        form.resetFields();
    };

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <h1>{t('appointment:calendarTitle')}</h1>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                        {t('appointment:scheduleButton')}
                    </Button>
                </div>
                <Calendar dateCellRender={dateCellRender} />
            </Card>

            <Modal
                title={t('appointment:scheduleAppointment')}
                open={modalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={700}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {/* Patient type toggle */}
                    <Form.Item label={t('appointment:patient')}>
                        <Radio.Group
                            value={patientType}
                            onChange={(e) => {
                                setPatientType(e.target.value);
                                form.resetFields(['patientId', 'newPatientName', 'newPatientAge', 'newPatientGender', 'newPatientContact', 'newPatientEmail']);
                            }}
                            style={{ marginBottom: 12 }}
                        >
                            <Radio.Button value="existing">
                                <UserOutlined style={{ marginRight: 4 }} />
                                {t('appointment:existingPatient', 'Existing Patient')}
                            </Radio.Button>
                            <Radio.Button value="new">
                                <UserAddOutlined style={{ marginRight: 4 }} />
                                {t('appointment:newPatient', 'New Patient')}
                            </Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {/* Existing patient selector */}
                    {patientType === 'existing' && (
                        <Form.Item name="patientId" label={t('appointment:selectPatient')} rules={[{ required: true, message: t('appointment:selectPatient') }]}>
                            <Select placeholder={t('appointment:selectPatient')} showSearch optionFilterProp="children">
                                {patients.map(p => (
                                    <Option key={p._id} value={p._id}>{p.name} - {p.contact}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {/* New patient fields */}
                    {patientType === 'new' && (
                        <>
                            <Divider style={{ margin: '8px 0 16px' }}>{t('appointment:newPatientDetails', 'New Patient Details')}</Divider>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="newPatientName"
                                        label={t('patient:fullName')}
                                        rules={[{ required: true, message: t('patient:validation.nameRequired') }]}
                                    >
                                        <Input placeholder={t('patient:placeholders.name')} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="newPatientAge"
                                        label={t('patient:age')}
                                        rules={[{ required: true, message: t('patient:validation.ageRequired') }]}
                                    >
                                        <Input type="number" placeholder={t('patient:placeholders.age')} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="newPatientGender"
                                        label={t('patient:gender')}
                                        rules={[{ required: true, message: t('patient:validation.genderRequired') }]}
                                    >
                                        <Select placeholder={t('patient:selectGender')}>
                                            <Option value="Male">{t('patient:male')}</Option>
                                            <Option value="Female">{t('patient:female')}</Option>
                                            <Option value="Other">{t('patient:other')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="newPatientContact"
                                        label={t('patient:contact')}
                                        rules={[{ required: true, message: t('patient:validation.contactRequired') }]}
                                    >
                                        <Input placeholder={t('patient:placeholders.contact')} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="newPatientEmail"
                                        label={t('patient:email')}
                                        rules={[{ type: 'email', message: t('patient:validation.emailInvalid') }]}
                                    >
                                        <Input placeholder={t('patient:placeholders.email')} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Divider style={{ margin: '8px 0 16px' }} />
                        </>
                    )}

                    {!isDentist && (
                        <Form.Item name="dentistId" label={t('appointment:dentist')} rules={[{ required: true }]}>
                            <Select placeholder={t('appointment:selectDentist')}>
                                {dentists.map(d => (
                                    <Option key={d._id} value={d._id}>{d.name} - {d.specialization}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <Form.Item name="date" label={t('appointment:date')} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="time" label={t('appointment:time')} rules={[{ required: true }]}>
                        <TimePicker format="HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="type" label={t('appointment:type')} rules={[{ required: true }]}>
                        <Select placeholder={t('appointment:selectType')}>
                            <Option value="Consultation">{t('appointment:consultation')}</Option>
                            <Option value="Follow-up">{t('appointment:followUp')}</Option>
                            <Option value="Emergency">{t('appointment:emergency')}</Option>
                            <Option value="Routine Checkup">{t('appointment:routineCheckup')}</Option>
                            <Option value="Treatment">{t('appointment:treatment')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reasonForVisit" label={t('appointment:reasonForVisit')} rules={[{ required: true }]}>
                        <TextArea rows={3} placeholder={t('appointment:describeReason')} />
                    </Form.Item>
                    <Form.Item name="notes" label={t('appointment:notes')}>
                        <TextArea rows={2} placeholder={t('appointment:additionalNotes')} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={submitting}>
                            {t('appointment:scheduleButton')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Appointments;
